import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

/**
 * Creates a DOM element to be used as a React root.
 *
 * @param {string} id - ID for the root element.
 * @returns {HTMLElement} The created root element.
 */
export function createRootElement(id: string) {
	const rootContainer = document.createElement("div")
	rootContainer.setAttribute("id", id)
	return rootContainer
}

/**
 * Appends an element as the last child of the body.
 *
 * @param {HTMLElement} rootElem - The element to be appended.
 */
function addRootElement(rootElem: HTMLDivElement) {
	document.body.insertBefore(
		rootElem,
		(document.body.lastElementChild as HTMLDivElement).nextElementSibling
	)
}

/**
 * Custom hook to create a portal.
 *
 * @param {string} id - ID of the target element.
 * @returns {HTMLElement} DOM node for the Portal target.
 */
export function usePortal(id: string) {
	const rootElemRef = useRef<HTMLDivElement | null>(null)

	useEffect(function setupElement() {
		const existingParent = document.querySelector(`#${id}`)
		const parentElem = existingParent || createRootElement(id)

		if (!existingParent) {
			addRootElement(parentElem as HTMLDivElement)
		}

		parentElem.appendChild(rootElemRef.current as HTMLElement)

		return function removeElement() {
			parentElem.remove()
		}
	}, [])

	function getRootElem() {
		if (!rootElemRef.current) {
			rootElemRef.current = document.createElement("div")
		}
		return rootElemRef.current
	}

	return getRootElem()
}

/**
 * Props for the `Portal` component.
 */
type PortalProps = {
	id: string
	children: JSX.Element | JSX.Element[]
}

/**
 * Renders children into a different DOM node.
 * Uses the `usePortal` hook to handle DOM interactions.
 *
 * @author fredByOcode
 *
 * @example
 * ```jsx
 * <Portal id="modal-root">
 *   <Modal>
 *     // modal content
 *   </Modal>
 * </Portal>
 * ```
 *
 * @param {PortalProps} props - The properties for the Portal component.
 * @returns {JSX.Element} The React portal.
 */
const Portal = ({ id, children }: PortalProps) => {
	const target = usePortal(id)
	return createPortal(children, target)
}

export default Portal
